import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  Send,
  Play,
  UserCheck,
  User,
  Briefcase,
  Shield,
  Save,
  Sparkles,
  Eye,
  FileText,
} from "lucide-react";
import DatePickerField from "../../components/common/DatePickerField";
import {
  DepartmentSelect,
  DesignationSelect,
  LocationSelect,
} from "../../components/common/OrgStructureSelects";
import { getNextEmployeeCode } from "../Employees/EmployeeApi";
import { EMPLOYEE_TYPES, DEFAULT_EMPLOYEE_TYPE } from "../Employees/employeeTypeConstants";
import {
  getOnboarding,
  updateOnboarding,
  submitOnboarding,
  startOnboarding,
  activateOnboarding,
  updateOnboardingTask,
  updateOnboardingDocument,
  onboardingApprovalAction,
  formatLifecycleError,
  uploadOnboardingDocument,
  getLifecycleDocumentUrl,
  getLifecycleDocumentName,
} from "./lifecycleApi";

const TABS = [
  { id: "Details", icon: User },
  { id: "Documents", icon: Briefcase },
  { id: "Tasks", icon: CheckCircle },
  { id: "Approvals", icon: Shield },
  { id: "Timeline", icon: Sparkles },
];

const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-700 ring-slate-200",
  "Pending Approval": "bg-amber-50 text-amber-800 ring-amber-200",
  Approved: "bg-blue-50 text-blue-800 ring-blue-200",
  "In Progress": "bg-violet-50 text-violet-800 ring-violet-200",
  Completed: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  "Employee Activated": "bg-green-50 text-green-800 ring-green-200",
};

const inputCls =
  "w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-white text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500/25 focus:border-violet-400 transition placeholder:text-slate-400";
const selectCls =
  "w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-white text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500/25 focus:border-violet-400 transition appearance-none cursor-pointer";
const codeCls =
  "w-full border border-violet-200 rounded-xl px-3.5 py-2.5 text-sm bg-violet-50/60 text-violet-900 font-semibold tracking-wide shadow-sm cursor-not-allowed";

function FormField({ label, hint, children, required }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {label}
          {required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function LcOnboardingDetail() {
  const { id } = useParams();
  const [ob, setOb] = useState(null);
  const [tab, setTab] = useState("Details");
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [codeLoading, setCodeLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const isLocked = ob?.status === "Employee Activated";

  const handleDocumentReject = async (documentId) => {
    const reason = window.prompt("Reason for rejection (required):", "");
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("Please provide a rejection reason");
      return;
    }
    await action(() =>
      updateOnboardingDocument(id, documentId, {
        status: "Rejected",
        rejection_reason: trimmed,
      })
    );
  };

  const handleDocumentUpload = async (documentId, file) => {
    setUploadingDocId(documentId);
    setError("");
    try {
      await uploadOnboardingDocument(id, documentId, file);
      await load();
    } catch (e) {
      setError(formatLifecycleError(e, "Upload failed"));
    } finally {
      setUploadingDocId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOnboarding(id);
      setOb(data);
      setForm({
        employee_name: data.employee_name || "",
        email: data.email || "",
        mobile_number: data.mobile_number || "",
        employee_type: data.employee_type || DEFAULT_EMPLOYEE_TYPE,
        previous_experience_years: data.previous_experience_years ?? 0,
        date_of_joining: data.date_of_joining || "",
        probation_end_date: data.probation_end_date || "",
        employee_code: data.employee_code || "",
        department_id: data.department_id || "",
        designation_id: data.designation_id || "",
        location_id: data.location_id || "",
        reporting_manager_id: data.reporting_manager_id || "",
        compensation_json: data.compensation_json || {},
        compliance_json: data.compliance_json || {},
        personal_details_json: data.personal_details_json || {},
        bank_details_json: data.bank_details_json || {},
      });
      if (!data.employee_code && data.status !== "Employee Activated") {
        setCodeLoading(true);
        getNextEmployeeCode()
          .then(({ employee_code }) => {
            if (employee_code) {
              setForm((prev) => ({ ...prev, employee_code }));
            }
          })
          .finally(() => setCodeLoading(false));
      }
    } catch (e) {
      setError(formatLifecycleError(e, "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveDetails = async () => {
    setSaving(true);
    setError("");
    try {
      await updateOnboarding(id, form);
      await load();
    } catch (e) {
      setError(formatLifecycleError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const action = async (fn) => {
    try {
      setError("");
      await fn();
      await load();
    } catch (e) {
      setError(formatLifecycleError(e, "Action failed"));
    }
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <div className="animate-pulse text-sm text-slate-500">Loading onboarding…</div>
      </div>
    );
  }

  if (!ob) {
    const msg = typeof error === "string" ? error : formatLifecycleError({ response: { data: { detail: error } } }, "Not found");
    return <p className="text-red-600">{msg || "Not found"}</p>;
  }

  const statusClass = STATUS_STYLES[ob.status] || STATUS_STYLES.Draft;

  return (
    <div className="space-y-5 pb-8">
      {/* Header */}
      <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-bold text-slate-900">{ob.employee_name || "New Employee Onboarding"}</h2>
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${statusClass}`}>
                  {ob.status}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {form.employee_code ? (
                  <span className="font-mono font-medium text-violet-700">{form.employee_code}</span>
                ) : (
                  "No employee code"
                )}
                {ob.email && <span className="mx-2 text-slate-300">·</span>}
                {ob.email}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {ob.status === "Draft" && (
              <button
                type="button"
                onClick={() => action(() => submitOnboarding(id))}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium shadow-sm transition"
              >
                <Send className="w-4 h-4" /> Submit for Approval
              </button>
            )}
            {(ob.status === "Approved" || ob.status === "Draft") && !isLocked && (
              <button
                type="button"
                onClick={() => action(() => startOnboarding(id))}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow-sm transition"
              >
                <Play className="w-4 h-4" /> Start Onboarding
              </button>
            )}
            {!isLocked && (
              <button
                type="button"
                onClick={() => action(() => activateOnboarding(id))}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium shadow-sm transition"
              >
                <UserCheck className="w-4 h-4" /> Activate Employee
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {typeof error === "string" ? error : formatLifecycleError({ response: { data: { detail: error } } })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100/80 border border-slate-200/60">
        {TABS.map(({ id: tabId, icon: Icon }) => (
          <button
            key={tabId}
            type="button"
            onClick={() => setTab(tabId)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
              tab === tabId
                ? "bg-white text-violet-700 shadow-sm ring-1 ring-slate-200/80"
                : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
            }`}
          >
            <Icon className="w-4 h-4" />
            {tabId}
          </button>
        ))}
      </div>

      {tab === "Details" && (
        <div className="space-y-5">
          <SectionCard title="Employee Information" subtitle="Core profile used for employee activation">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <FormField
                label="Employee Code"
                hint={isLocked ? "Cannot be changed after activation" : "Auto-generated from database sequence · same as Add Employee"}
              >
                <input
                  readOnly
                  className={codeCls}
                  placeholder={codeLoading ? "Generating…" : "BA0001"}
                  value={codeLoading ? "" : form.employee_code || ""}
                />
              </FormField>

              <FormField label="Employee Name" required>
                <input
                  className={inputCls}
                  value={form.employee_name || ""}
                  onChange={(e) => setField("employee_name", e.target.value)}
                  disabled={isLocked}
                  placeholder="Full name"
                />
              </FormField>

              <FormField label="Email" required>
                <input
                  type="email"
                  className={inputCls}
                  value={form.email || ""}
                  onChange={(e) => setField("email", e.target.value)}
                  disabled={isLocked}
                  placeholder="work@company.com"
                />
              </FormField>

              <FormField label="Mobile">
                <input
                  className={inputCls}
                  value={form.mobile_number || ""}
                  onChange={(e) => setField("mobile_number", e.target.value)}
                  disabled={isLocked}
                  placeholder="+91 …"
                />
              </FormField>

              <FormField label="Employee Type" required>
                <select
                  className={selectCls}
                  value={form.employee_type || DEFAULT_EMPLOYEE_TYPE}
                  onChange={(e) => setField("employee_type", e.target.value)}
                  disabled={isLocked}
                >
                  {EMPLOYEE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </FormField>

              <DepartmentSelect
                label="Department"
                value={form.department_id}
                onChange={(id) => setField("department_id", id || "")}
                disabled={isLocked}
              />

              <DesignationSelect
                label="Designation"
                value={form.designation_id}
                onChange={(id) => setField("designation_id", id || "")}
                disabled={isLocked}
              />

              <LocationSelect
                label="Work location"
                value={form.location_id}
                onChange={(id) => setField("location_id", id || "")}
                disabled={isLocked}
              />

              <FormField label="Previous Experience (Years)" hint="Relieving letter required if &gt; 0">
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  className={inputCls}
                  value={form.previous_experience_years ?? 0}
                  onChange={(e) => setField("previous_experience_years", e.target.value)}
                  disabled={isLocked}
                />
              </FormField>

              <FormField label="Date of Joining">
                <DatePickerField
                  hideLabel
                  value={form.date_of_joining || ""}
                  onChange={(d) => setField("date_of_joining", d)}
                  placeholder="Select joining date"
                  disabled={isLocked}
                  inputClassName={inputCls}
                />
              </FormField>

              <FormField label="Probation End Date">
                <DatePickerField
                  hideLabel
                  value={form.probation_end_date || ""}
                  onChange={(d) => setField("probation_end_date", d)}
                  placeholder="Select probation end"
                  min={form.date_of_joining || undefined}
                  disabled={isLocked}
                  inputClassName={inputCls}
                />
              </FormField>

              <FormField label="Date of Birth" hint="Required before employee activation">
                <DatePickerField
                  hideLabel
                  birthdate
                  value={form.personal_details_json?.date_of_birth || ""}
                  onChange={(d) =>
                    setForm((prev) => ({
                      ...prev,
                      personal_details_json: { ...prev.personal_details_json, date_of_birth: d },
                    }))
                  }
                  placeholder="Select date of birth"
                  disabled={isLocked}
                  inputClassName={inputCls}
                />
              </FormField>
            </div>
          </SectionCard>

          <SectionCard title="Compensation & Compliance" subtitle="Payroll and statutory details">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <FormField label="CTC (Annual)">
                <input
                  className={inputCls}
                  value={form.compensation_json?.ctc || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      compensation_json: { ...prev.compensation_json, ctc: e.target.value },
                    }))
                  }
                  disabled={isLocked}
                  placeholder="e.g. 600000"
                />
              </FormField>
              <FormField label="Pay Cycle">
                <select
                  className={selectCls}
                  value={form.compensation_json?.pay_cycle || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      compensation_json: { ...prev.compensation_json, pay_cycle: e.target.value },
                    }))
                  }
                  disabled={isLocked}
                >
                  <option value="">Select</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Bi-Weekly">Bi-Weekly</option>
                  <option value="Weekly">Weekly</option>
                </select>
              </FormField>
              <FormField label="PAN">
                <input
                  className={inputCls}
                  value={form.compliance_json?.pan || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      compliance_json: { ...prev.compliance_json, pan: e.target.value },
                    }))
                  }
                  disabled={isLocked}
                  placeholder="ABCDE1234F"
                />
              </FormField>
              <FormField label="Aadhaar">
                <input
                  className={inputCls}
                  value={form.compliance_json?.aadhaar || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      compliance_json: { ...prev.compliance_json, aadhaar: e.target.value },
                    }))
                  }
                  disabled={isLocked}
                  placeholder="XXXX XXXX XXXX"
                />
              </FormField>
            </div>
          </SectionCard>

          {Number(form.previous_experience_years) > 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span className="font-medium">Experienced hire:</span>
              Previous Company Relieving Letter is mandatory before activation.
            </div>
          )}

          {!isLocked && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={saveDetails}
                disabled={saving || codeLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-semibold shadow-sm transition"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving…" : "Save Details"}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "Documents" && (
        <SectionCard title="Document Collection" subtitle="Resume mandatory · Relieving letter for experienced hires">
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <th className="pb-3 pr-4 font-semibold">Document</th>
                  <th className="pb-3 pr-4 font-semibold">Type</th>
                  <th className="pb-3 pr-4 font-semibold">Uploaded File</th>
                  <th className="pb-3 pr-4 font-semibold">Status</th>
                  <th className="pb-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(ob.documents || []).map((doc) => {
                  const fileName = getLifecycleDocumentName(doc);
                  const fileUrl = getLifecycleDocumentUrl(doc.document_url);
                  const isUploading = uploadingDocId === doc.document_id;
                  const hasFile = Boolean(fileUrl && fileName);

                  return (
                    <tr key={doc.document_id} className="hover:bg-slate-50/50">
                      <td className="py-3 pr-4 font-medium text-slate-800">
                        {doc.document_type}
                        {doc.is_mandatory && <span className="text-red-500 ml-0.5">*</span>}
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${doc.is_mandatory ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                          {doc.is_mandatory ? "Mandatory" : "Optional"}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        {hasFile ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                            <span className="text-xs text-slate-700 truncate max-w-[180px]" title={fileName}>
                              {fileName}
                            </span>
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-900 shrink-0"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </a>
                          </div>
                        ) : doc.document_url ? (
                          <span className="text-xs text-amber-700">Re-upload required (old stub file)</span>
                        ) : (
                          <span className="text-xs text-slate-400">No file uploaded</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="space-y-1">
                          <span
                            className={`inline-flex text-xs font-medium px-2.5 py-1 rounded-full ${
                              doc.status === "Verified"
                                ? "bg-emerald-100 text-emerald-800"
                                : doc.status === "Uploaded"
                                  ? "bg-blue-100 text-blue-800"
                                  : doc.status === "Rejected"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {doc.status}
                          </span>
                          {doc.status === "Rejected" && doc.rejection_reason && (
                            <p className="text-xs text-red-600 max-w-[200px]" title={doc.rejection_reason}>
                              {doc.rejection_reason}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {!isLocked && (
                            <label className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium transition ${
                              isUploading
                                ? "bg-slate-100 text-slate-500 cursor-wait"
                                : "bg-violet-50 text-violet-700 cursor-pointer hover:bg-violet-100"
                            }`}>
                              {isUploading ? "Uploading…" : hasFile ? "Replace" : "Upload"}
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,application/pdf,image/*"
                                className="hidden"
                                disabled={isUploading}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = "";
                                  if (!file) return;
                                  handleDocumentUpload(doc.document_id, file);
                                }}
                              />
                            </label>
                          )}
                          {doc.status === "Uploaded" && !isLocked && (
                            <button
                              type="button"
                              className="text-xs font-medium text-emerald-700 px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100"
                              onClick={() =>
                                action(() => updateOnboardingDocument(id, doc.document_id, { status: "Verified" }))
                              }
                            >
                              Verify
                            </button>
                          )}
                          {["Uploaded", "Verified"].includes(doc.status) && !isLocked && hasFile && (
                            <button
                              type="button"
                              className="text-xs font-medium text-red-700 px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100"
                              onClick={() => handleDocumentReject(doc.document_id)}
                            >
                              Reject
                            </button>
                          )}
                          {doc.status === "Rejected" && !isLocked && (
                            <span className="text-xs text-slate-500">Re-upload required</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {tab === "Tasks" && (
        <div className="space-y-2">
          {(ob.tasks || []).map((task) => (
            <div
              key={task.task_id}
              className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-300 transition"
            >
              <div>
                <div className="font-medium text-sm text-slate-800">{task.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {task.category} · {task.priority} · {task.is_mandatory ? "Mandatory" : "Optional"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    task.status === "Completed" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {task.status}
                </span>
                {task.status !== "Completed" && (
                  <button
                    type="button"
                    onClick={() => action(() => updateOnboardingTask(id, task.task_id, { status: "Completed" }))}
                    className="p-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition"
                    title="Mark complete"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Approvals" && (
        <div className="space-y-2">
          {(ob.approvals || []).map((ap) => (
            <div
              key={ap.approval_id}
              className="rounded-xl border border-slate-200 bg-white p-4 flex justify-between items-center shadow-sm"
            >
              <div>
                <div className="font-medium text-sm text-slate-800">{ap.approval_role}</div>
                <div className="text-xs text-slate-500">Step {ap.sequence} · {ap.status}</div>
              </div>
              {ap.status === "Pending" && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition"
                    onClick={() => action(() => onboardingApprovalAction(id, ap.approval_id, { action: "Approve" }))}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                    onClick={() => action(() => onboardingApprovalAction(id, ap.approval_id, { action: "Reject" }))}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "Timeline" && (
        <SectionCard title="Activity Timeline">
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-100">
              <dt className="text-slate-500">Created</dt>
              <dd className="font-medium text-slate-800">{new Date(ob.created_at).toLocaleString()}</dd>
            </div>
            {ob.activated_at && (
              <div className="flex justify-between py-2 border-b border-slate-100">
                <dt className="text-slate-500">Activated</dt>
                <dd className="font-medium text-emerald-700">{new Date(ob.activated_at).toLocaleString()}</dd>
              </div>
            )}
            <div className="flex justify-between py-2">
              <dt className="text-slate-500">Last updated</dt>
              <dd className="font-medium text-slate-800">{new Date(ob.updated_at).toLocaleString()}</dd>
            </div>
          </dl>
        </SectionCard>
      )}
    </div>
  );
}
